import "./styles/bigwa-page-padding-fix.css";
import "./styles/bigwa-font-scaling.css";
import "./styles/bigwa-teoria-fix.css";
import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import { logger } from './utils/logger';
import * as Sentry from '@sentry/react';
import { Route, Switch, useLocation } from 'wouter';
import { Toaster } from 'react-hot-toast';
import { bigwaToast } from './utils/bigwaToast';
import { useShallow } from 'zustand/shallow';
import { useAuthStore } from './stores/authStore';
import { useUIStore } from './stores/uiStore';
import { useTaskStore } from './stores/taskStore';
import { useDriveProjectStore } from './stores/driveProjectStore';
import { useLibraryStore } from './stores/libraryStore';
import { usePreferencesStore } from './stores/preferencesStore';
import { googleDrive } from './services/googleDrive';
import LandingPage from './pages/LandingPage';
import { SkeletonList } from './components/Skeleton';
const QuickFind = lazy(() => import('./components/QuickFind'));
const QuickAddModal = lazy(() => import('./components/QuickAddModal').then(m => ({ default: m.QuickAddModal })));
// Prefetch QuickAddModal chunk em idle time para abertura instantânea
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => { import('./components/QuickAddModal'); });
}
import { useQuickAdd } from './hooks/useQuickAdd';
import { useGlobalPdfDrop } from './hooks/useGlobalPdfDrop';
import { GlobalPdfDropOverlay } from './components/GlobalPdfDropOverlay';
import { BigwaStatusPanel } from './components/BigwaStatusPanel';
import { useBigwaConnector } from './hooks/useBigwaConnector';
const PreferenciasModal = lazy(() => import('./components/PreferenciasModal'));
const ScratchpadPanel = lazy(() => import('./components/ScratchpadPanel'));
const TaskPanel = lazy(() => import('./components/TaskPanel'));
const AiLibraryChatWindow = lazy(() => import('./components/AiLibraryChatWindow').then(m => ({ default: m.AiLibraryChatWindow })));
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt').then(m => ({ default: m.PWAInstallPrompt })));
// NotificationPanel substituído por BigwaStatusPanel (Fase 1 Não-Intrusividade)
import { useScratchpadStore } from './stores/useScratchpadStore';
import { useTaskPanelStore } from './stores/useTaskPanelStore';
import { useAiChatWindowStore } from './stores/aiChatWindowStore';
import { useOutlineStore } from './stores/outlineStore';
import { useSumarioComentadoStore } from './stores/sumarioComentadoStore';
import { Analytics, track } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BigwaDegradedBanner } from './components/BigwaDegradedBanner';
import { useDriveInit } from './hooks/useDriveInit';
import { useTabLeader } from './hooks/useTabLeader';
import { useDeviceClass } from './hooks/useDeviceClass';
import { RecoveryScreen } from './components/RecoveryScreen';
import { TabBlockedOverlay } from './components/TabBlockedOverlay';
import { AppLoadingScreen } from './components/AppLoadingScreen';
import { trackPWASession } from './hooks/usePWAEngagement';

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DadosPesquisaV2 = lazy(() => import('./pages/DadosPesquisaV2'));
const ArchivePage = lazy(() => import('./pages/Archive'));
const TrashPage = lazy(() => import('./pages/Trash'));
const Library = lazy(() => import('./pages/Library'));
const LibraryTrash = lazy(() => import('./pages/LibraryTrash'));
const LibraryStats = lazy(() => import('./pages/LibraryStats').then(m => ({ default: m.LibraryStats })));
const Repository = lazy(() => import('./pages/Repository'));
const Leituras = lazy(() => import('./pages/Leituras'));
const DadosPesquisa = lazy(() => import('./pages/DadosPesquisa'));
const GrupoPage = lazy(() => import('./pages/GrupoPage'));
const TecerTexto = lazy(() => import('./pages/TecerTexto'));
const Conexoes = lazy(() => import('./pages/Conexoes'));
const Citations = lazy(() => import('./pages/Citations'));
const Indicacoes = lazy(() => import('./pages/Indicacoes'));
const Preferencias = lazy(() => import('./pages/Preferencias'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SharedBiblioteca = lazy(() => import('./pages/SharedBiblioteca'));
const Colaboracao = lazy(() => import('./pages/Colaboracao'));
// Rascunho removido — arquivos mantidos como código morto
// const Rascunho = lazy(() => import('./pages/Rascunho'));
const PdfReaderPage = lazy(() => import('./pages/PdfReaderPage'));

// Mobile PWA pages (lazy-loaded — zero impact on desktop entry bundle)
const MobileApp = lazy(() => import('./pages/mobile/MobileApp'));

// Tablet app (separado, espelha o Mobile — lazy, chunk isolado, zero peso no entry desktop)
const TabletApp = lazy(() => import('./pages/tablet/TabletApp'));

// Public pages (no auth required)
const TermosPage = lazy(() => import('./pages/TermosPage'));
const PrivacidadePage = lazy(() => import('./pages/PrivacidadePage'));
const AccessDenied = lazy(() => import('./pages/AccessDenied'));

// Preload das páginas principais em background após login
// Grupo 1: páginas mais visitadas (imediato após 800ms)
// Grupo 2: páginas secundárias (via requestIdleCallback para não competir)
const preloadMainPages = () => {
  setTimeout(() => {
    import('./pages/Dashboard');
    import('./pages/Library');
    import('./pages/Leituras');

    const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 200);
    idle(() => {
      import('./pages/Conexoes');
      import('./pages/Citations');
      import('./pages/Repository');
      import('./pages/DadosPesquisa');
      import('./pages/TecerTexto');
      import('./pages/DadosPesquisaV2');
    });
  }, 800);
};

// Suspense fallback: skeleton shimmer matching content background.
// Since pages are prefetched, this is rarely seen.
const PageLoader = () => (
  <div style={{ minHeight: '100vh', background: '#FFFFFF', padding: '24px' }}>
    <SkeletonList count={6} />
  </div>
);

// Route transition: simple fade-in on navigation. Page swap is instant.
const RouteTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location] = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove('page-enter');
    void el.offsetHeight; // force reflow to retrigger animation
    el.classList.add('page-enter');
  }, [location]);

  return (
    <div ref={ref} className="page-enter">
      {children}
    </div>
  );
};

// ✅ SIDEBAR PROGRESSIVA: Proteção de rotas que exigem projeto ativo
const RequireProject: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getActiveProject = useDriveProjectStore(state => state.getActiveProject);
  const isLoading = useDriveProjectStore(state => state.isLoading);
  const [, navigate] = useLocation();
  
  useEffect(() => {
    if (!isLoading && !getActiveProject()) {
      navigate('/');
      bigwaToast.info('Crie uma mesa de trabalho para acessar esta página', { icon: '📁' });
    }
  }, [isLoading, getActiveProject, navigate]);
  
  if (!getActiveProject()) return null;
  return <>{children}</>;
};

// Sentry ErrorBoundary fallback — quiet luxury, no stack trace
const ErrorFallback = ({ resetError }: { resetError: () => void }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FAFAFA',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}>
    <div style={{
      textAlign: 'center',
      maxWidth: '400px',
      padding: '48px 32px',
    }}>
      <div style={{ fontSize: '14px', color: '#999', letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: '16px' }}>
        Bigwa
      </div>
      <h2 style={{ fontSize: '20px', fontWeight: 500, color: '#333', margin: '0 0 12px' }}>
        Algo inesperado aconteceu
      </h2>
      <p style={{ fontSize: '14px', color: '#777', lineHeight: 1.6, margin: '0 0 32px' }}>
        Nosso time foi notificado automaticamente.<br />
        Tente recarregar a página.
      </p>
      <button
        onClick={resetError}
        style={{
          background: '#477B72',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 32px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseOver={e => (e.currentTarget.style.background = '#3a6960')}
        onMouseOut={e => (e.currentTarget.style.background = '#477B72')}
      >
        Recarregar
      </button>
    </div>
  </div>
);

// Feature flag — instrumentação do gate de bloqueio de tablet (Vercel Analytics, evento custom).
// OFF por padrão (Pilar 4 / Reitor 11): o evento só liga com confirmação de gasto de Adrian
// (plano Vercel que suporte custom events + folga de quota). Enquanto OFF, o bloqueio (a função)
// independe disto — a prova do gate recai sobre a matriz manual (acceptance C1/H).
// Ligar = trocar para true + redeploy. Kill-switch = voltar a false + redeploy.
const TABLET_BLOCKED_ANALYTICS_ENABLED: boolean = false;

function App() {
  const { isLeader, claimLeadership } = useTabLeader();
  const { user, isAuthenticating, isInitialized, initialize } = useAuthStore(
    useShallow(s => ({ user: s.user, isAuthenticating: s.isAuthenticating, isInitialized: s.isInitialized, initialize: s.initialize }))
  );
  const [location, setLocation] = useLocation();
  const [isQuickFindOpen, setIsQuickFindOpen] = useState(false);
  const [isDriveInitializing, setIsDriveInitializing] = useState(false);
  const [driveInitError, setDriveInitError] = useState<string | null>(null);
  const quickAdd = useQuickAdd();
  const preloadedRef = useRef(false);
  
  // Preferências Modal
  const [isPreferenciasOpen, setIsPreferenciasOpen] = useState(false);
  const [preferencesTab, setPreferencesTab] = useState<"geral" | "acessibilidade" | "anotacoes" | "integracoes" | "conta">("geral");
  
  // Drive resilience state
  const { isRecoveryNeeded } = useDriveInit();

  // Classe de dispositivo — gate de bloqueio de tablet (Sprint 005).
  // Mesmo combinado que Dashboard.tsx e a telemetria de useDeviceClass.ts já derivam.
  const { isTouch, isTabletSize } = useDeviceClass();
  const isTabletDevice = isTouch && isTabletSize; // isTouch === hasPrimaryTouch()

  // Floating windows backdrop — tom verde Bigwa no fundo
  const isScratchpadOpen = useScratchpadStore(s => s.isOpen);
  const isTaskPanelOpen = useTaskPanelStore(s => s.isOpen);
  const isAiChatOpen = useAiChatWindowStore(s => s.isOpen && !s.isMinimized);
  const isSumarioOpen = useSumarioComentadoStore(s => s.panelState.isOpen);
  const isOutlineOpen = useOutlineStore(s => s.panelState.isOpen);
  // isNotificationPanelVisible removido — BigwaStatusPanel gerencia própria visibilidade
  const anyFloatingWindowOpen = isScratchpadOpen || isTaskPanelOpen || isAiChatOpen || isOutlineOpen || isSumarioOpen;

  // Sincronizar modais locais com uiStore.openModalCount
  // para que o GlobalPdfDrop saiba quando desativar
  const { registerModal, unregisterModal } = useUIStore(
    useShallow(s => ({ registerModal: s.registerModal, unregisterModal: s.unregisterModal }))
  );
  useEffect(() => {
    const anyModalOpen = isQuickFindOpen || quickAdd.isOpen || isPreferenciasOpen;
    if (anyModalOpen) {
      registerModal();
      return () => unregisterModal();
    }
  }, [isQuickFindOpen, quickAdd.isOpen, isPreferenciasOpen, registerModal, unregisterModal]);

  // PWA standalone detection (para lazy-load guard do PWAInstallPrompt)
  const isStandalone = typeof window !== 'undefined'
    && (window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as unknown as Record<string, unknown>).standalone === true);

  // Bigwa Connector - recebe referências da extensão Chrome
  useBigwaConnector();

  // Global PDF drag & drop
  const globalDrop = useGlobalPdfDrop();
  
  // Detectar página atual para contexto do QuickAdd
  const getCurrentPage = (): 'home' | 'library' | 'repository' | 'leituras' | 'other' => {
    if (location === '/') return 'home';
    if (location === '/library' || location.startsWith('/library/')) return 'library';
    if (location === '/repository') return 'repository';
    if (location === '/leituras') return 'leituras';
    return 'other';
  };

  // Inicializar auth na montagem do componente
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Registrar sessão para PWA engagement tracking
  useEffect(() => {
    trackPWASession();
  }, []);

  // Registrar Service Worker para PWA com detecção de update + version.json check
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let registration: ServiceWorkerRegistration | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let refreshing = false;

    // Recarregar se detectar versão nova (via SW ou version.json)
    const triggerReload = () => {
      if (refreshing) return;
      refreshing = true;
      bigwaToast.info('Atualizando para nova versão...', { icon: '🔄', duration: 2000 });
      setTimeout(() => window.location.reload(), 1500);
    };

    // Checar version.json (funciona independente do SW — rede de segurança)
    // Em dev (localhost), o Vite plugin regera version.json a cada build com novo buildTime,
    // causando mismatch com __APP_BUILD_TIME__ compilado no JS → reload infinito.
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const checkVersionJson = async () => {
      if (isDev) return; // Em dev, version.json muda a cada build — skip para evitar reload loop
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`);
        if (!res.ok) return;
        const remote = await res.json();
        if (remote.buildTime && remote.buildTime !== __APP_BUILD_TIME__) {
          triggerReload();
        }
      } catch {
        // Offline ou erro de rede — ignorar silenciosamente
      }
    };

    navigator.serviceWorker.register('/sw.js').then((reg) => {
      registration = reg;

      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nova versão instalada — pedir ao SW para ativar
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });

      // Checagem periódica a cada 5 minutos (#34 — reduzido de 15min para detectar stale JS mais rápido em iPads PWA)
      intervalId = setInterval(() => {
        reg.update().catch(() => {});
        checkVersionJson();
      }, 5 * 60 * 1000);
    }).catch(() => {
      // SW registration falhou — ainda checar version.json periodicamente
      intervalId = setInterval(checkVersionJson, 5 * 60 * 1000);
    });

    // Checar update quando app volta ao foreground (iPad PWA backgrounded)
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (registration) registration.update().catch(() => {});
      checkVersionJson();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Quando o novo SW assume o controle, recarregar para usar a versão nova
    navigator.serviceWorker.addEventListener('controllerchange', triggerReload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      navigator.serviceWorker.removeEventListener('controllerchange', triggerReload);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Listen for Cmd+K / Ctrl+K to open QuickFind
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsQuickFindOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Listener: Abrir Preferências via QuickFind
  useEffect(() => {
    const handleOpenPreferencias = (e: CustomEvent<{ tab?: string }>) => {
      const tab = (e.detail?.tab || "geral") as typeof preferencesTab;
      setPreferencesTab(tab);
      setIsPreferenciasOpen(true);
    };
    window.addEventListener("open-preferencias", handleOpenPreferencias as EventListener);
    return () => window.removeEventListener("open-preferencias", handleOpenPreferencias as EventListener);
  }, []);
  // Listener: Toast de autoria detectada na captura
  useEffect(() => {
    const handleAuthorshipDetected = () => {
      bigwaToast.notify('Esta pode ser uma obra sua. Verifique em Minhas Obras.', {
        status: 'info',
        action: {
          label: 'Ver',
          intent: 'informative',
          onClick: () => setLocation('/library'),
        },
      });
    };
    window.addEventListener('bigwa:authorship-detected', handleAuthorshipDetected);
    return () => window.removeEventListener('bigwa:authorship-detected', handleAuthorshipDetected);
  }, [setLocation]);

  // Sync Google Tasks a cada 60 segundos
  const { syncWithGoogle, googleTasksEnabled } = useTaskStore(
    useShallow(s => ({ syncWithGoogle: s.syncWithGoogle, googleTasksEnabled: s.googleTasksEnabled }))
  );
  
  useEffect(() => {
    if (!googleTasksEnabled) return;
    
    // Sync inicial
    syncWithGoogle();
    
    // Polling a cada 60 segundos
    const interval = setInterval(() => {
      syncWithGoogle();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [googleTasksEnabled, syncWithGoogle]);


  // Recovery callback: re-trigger ensureAppStructure after user action
  const handleRecovery = React.useCallback(async () => {
    setIsDriveInitializing(true);
    setDriveInitError(null);
    try {
      const driveReady = await googleDrive.ensureAppStructure();
      if (!driveReady) {
        // Check if still in recovery (not a real error)
        const { driveConfig } = await import('./services/driveConfig');
        if (driveConfig.getState() === 'recovery-needed') {
          setIsDriveInitializing(false);
          return;
        }
        setDriveInitError('Não foi possível inicializar após recuperação.');
        setIsDriveInitializing(false);
        return;
      }
      const { loadProjects } = useDriveProjectStore.getState();
      await loadProjects();
      useLibraryStore.getState().loadWorks();
      useScratchpadStore.getState().loadNotes();
      setIsDriveInitializing(false);
      setLocation('/');
    } catch (error) {
      setDriveInitError('Erro ao conectar: ' + (error as Error).message);
      setIsDriveInitializing(false);
    }
  }, [setLocation]);

  // Carregar projetos quando user está autenticado
  // IMPORTANTE: Primeiro garante que a estrutura de pastas existe no Drive
  useEffect(() => {
    let isMounted = true;
    const initializeAfterLogin = async () => {
      if (user && isInitialized) {
        sessionStorage.removeItem('sidebarScroll');

        if (isMounted) {
          setIsDriveInitializing(true);
          setDriveInitError(null);
        }

        try {
          const driveReady = await googleDrive.ensureAppStructure();

          if (!isMounted) return;

          if (!driveReady) {
            // Check if recovery-needed (not an error)
            const { driveConfig } = await import('./services/driveConfig');
            if (driveConfig.getState() === 'recovery-needed') {
              // RecoveryScreen will appear via useDriveInit
              setIsDriveInitializing(false);
              return;
            }
            setDriveInitError('Não foi possível inicializar o Google Drive. Tente fazer logout e login novamente.');
            setIsDriveInitializing(false);
            return;
          }

          // Load preferences from Drive (cross-device sync)
          // fire-and-forget — não bloqueia init
          googleDrive.loadUserPreferences().then(drivePrefs => {
            if (!isMounted) return;
            if (drivePrefs) {
              usePreferencesStore.getState().mergeFromDrive(drivePrefs as Partial<import('./stores/preferencesStore').BigwaPreferences>);
            } else {
              // First time: upload localStorage preferences to Drive
              const prefs = usePreferencesStore.getState().preferences;
              googleDrive.saveUserPreferences(prefs as unknown as Record<string, unknown>).catch(err => {
                logger.error('[App] Falha ao fazer upload inicial de preferences:', err);
              });
            }
          }).catch(err => {
            logger.error('[App] Falha ao carregar preferences do Drive:', err);
          });

          // Iniciar loadWorks em paralelo com loadProjects (fire-and-forget)
          // loadWorks usa stale-while-revalidate via IndexedDB — UI popula rápido
          useLibraryStore.getState().loadWorks();

          // Carregar notas do Banco de Ideias para badge na ActionBar
          useScratchpadStore.getState().loadNotes();

          const { loadProjects } = useDriveProjectStore.getState();
          await loadProjects();

          if (!isMounted) return;

          setIsDriveInitializing(false);

          // Mobile routes (/m/*) handle their own navigation — don't redirect to /
          if (!window.location.pathname.startsWith('/m') && !window.location.pathname.startsWith('/tablet')) {
            setLocation('/');
          }

          if (!preloadedRef.current) {
            preloadedRef.current = true;
            preloadMainPages();
          }
        } catch (error) {
          logger.error('[App] Erro na inicialização:', error);
          if (isMounted) {
            setDriveInitError('Erro ao conectar com Google Drive: ' + (error as Error).message);
            setIsDriveInitializing(false);
          }
        }
      }
    };

    initializeAfterLogin();
    return () => { isMounted = false; };
  }, [user, isInitialized, setLocation]);

  // Instrumentação do gate de tablet (Vercel Analytics) — só booleanos derivados, sem PII (Regra 19).
  // FEATURE-FLAGGED OFF: ver TABLET_BLOCKED_ANALYTICS_ENABLED (Pilar 4 / Reitor 11). É hook —
  // fica na região de hooks, antes de qualquer return.
  useEffect(() => {
    if (TABLET_BLOCKED_ANALYTICS_ENABLED && isTabletDevice) {
      track('tablet_blocked', { hasPrimaryTouch: isTouch, isTabletSize });
    }
  }, [isTabletDevice, isTouch, isTabletSize]);

  // INVARIANTE DE POSIÇÃO: este gate de dispositivo é o PRIMEIRO early-return de RENDER
  // (acima de !isLeader, !isInitialized, /m, /tablet e RecoveryScreen). A supressão de redirect
  // (setLocation('/')) vive DENTRO do useEffect initializeAfterLogin acima e roda inócua — o
  // render é sempre interceptado aqui. NÃO mover para cima do useEffect (Regras dos Hooks).
  if (isTabletDevice) {
    return (
      <Suspense fallback={<AppLoadingScreen variant="auth" />}>
        <TabletApp />
      </Suspense>
    );
  }

  // Multi-tab protection: block non-leader tabs
  if (!isLeader) {
    return <TabBlockedOverlay onClaimLeadership={claimLeadership} />;
  }

  // Mostrar loading enquanto inicializa auth
  if (!isInitialized || isAuthenticating) {
    return <AppLoadingScreen variant="auth" />;
  }

  // Mobile PWA routes — renderiza MobileApp independente do Drive init
  if (location.startsWith('/m')) {
    return (
      <Suspense fallback={<AppLoadingScreen variant="auth" />}>
        <MobileApp />
      </Suspense>
    );
  }

  // Tablet routes — renderiza TabletApp (app separado) independente do Drive init.
  // INVARIANTE DE POSIÇÃO: este gate fica ACIMA do RecoveryScreen. Abaixo dele, um
  // fluxo de recovery jogaria o usuário de tablet ao desktop via setLocation('/') sem guard.
  if (location.startsWith('/tablet')) {
    return (
      <Suspense fallback={<AppLoadingScreen variant="auth" />}>
        <TabletApp />
      </Suspense>
    );
  }

  // Mostrar loading enquanto inicializa Google Drive (após login)
  if (user && isDriveInitializing) {
    return <AppLoadingScreen variant="drive" />;
  }

  // Recovery screen — pasta não encontrada
  if (user && isRecoveryNeeded && !isDriveInitializing) {
    return <RecoveryScreen onRecovered={handleRecovery} />;
  }

  // Mostrar erro se inicialização do Drive falhou
  if (user && driveInitError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-teal-700 to-yellow-600">
        <div className="bg-white rounded-lg p-8 max-w-md text-center shadow-xl">
          <div className="text-red-600 text-xl font-semibold mb-4">Erro de Inicialização</div>
          <div className="text-gray-700 mb-6">{driveInitError}</div>
          <button
            onClick={() => {
              // Fazer logout e tentar novamente
              const { logout } = useAuthStore.getState();
              logout(); googleDrive.reset();
              window.location.reload();
            }}
            className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors"
          >
            Fazer Logout e Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Public routes (no auth required)
  const publicRoutes = ['/termos', '/privacidade', '/access-denied'];
  const isPublicRoute = publicRoutes.includes(location);

  if (isPublicRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <RouteTransition>
          <Switch>
            <Route path="/termos">
              <TermosPage />
            </Route>
            <Route path="/privacidade">
              <PrivacidadePage />
            </Route>
            <Route path="/access-denied">
              <AccessDenied />
            </Route>
          </Switch>
        </RouteTransition>
      </Suspense>
    );
  }

  // Show landing page if not authenticated
  if (!user) {
    return <LandingPage />;
  }

  // Show main app if authenticated
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <BigwaDegradedBanner />
      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        // zIndex: topo absoluto — acima do reader (9999–10001), dos popovers
        // internos (999999) e das janelas flutuantes (1000000). Sem isso, todo
        // toast com o reader aberto era invisível (F2 smoke 4/jun); guardião em
        // src/__tests__/toaster-zindex.antireg.test.ts.
        containerStyle={{ bottom: 72, zIndex: 1000001 }}
        toastOptions={{
          duration: 3000,
          style: {
            background: '#323232',
            color: '#fff',
            borderRadius: '4px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '400',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.26)',
            maxWidth: '344px',
          },
          success: {
            duration: 2500,
            icon: ' ',
            style: {
              background: '#323232',
              color: '#fff',
            },
          },
          error: {
            duration: 4000,
            icon: ' ',
            style: {
              background: '#323232',
              color: '#fff',
            },
          },
          loading: {
            icon: ' ',
            style: {
              background: '#323232',
              color: '#fff',
            },
          },
        }}
      />
      <GlobalPdfDropOverlay
        dropState={globalDrop.dropState}
        processingProgress={globalDrop.processingProgress}
        onChooseReference={globalDrop.handleChooseReference}
        onChooseResearchData={globalDrop.handleChooseResearchData}
        onDismiss={globalDrop.handleDismissDisambiguate}
      />
      <BigwaStatusPanel />
      {isQuickFindOpen && (
        <Suspense fallback={null}>
          <QuickFind isOpen={isQuickFindOpen} onClose={() => setIsQuickFindOpen(false)} />
        </Suspense>
      )}
      {quickAdd.isOpen && (
        <Suspense fallback={null}>
          <QuickAddModal
            isOpen={quickAdd.isOpen}
            onClose={quickAdd.close}
            currentPage={getCurrentPage()}
          />
        </Suspense>
      )}
      {isPreferenciasOpen && (
        <Suspense fallback={null}>
          <PreferenciasModal
            isOpen={isPreferenciasOpen}
            onClose={() => setIsPreferenciasOpen(false)}
            initialTab={preferencesTab}
          />
        </Suspense>
      )}
      {anyFloatingWindowOpen && (
        <div
          className="floating-window-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--overlay-bg)',
            zIndex: 999,
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
          }}
        />
      )}
      {isScratchpadOpen && (
        <Suspense fallback={null}>
          <ScratchpadPanel />
        </Suspense>
      )}
      {isTaskPanelOpen && (
        <Suspense fallback={null}>
          <TaskPanel />
        </Suspense>
      )}
      {isAiChatOpen && (
        <ErrorBoundary section="AILibraryChat">
          <Suspense fallback={null}>
            <AiLibraryChatWindow />
          </Suspense>
        </ErrorBoundary>
      )}
      {!isStandalone && (
        <Suspense fallback={null}>
          <PWAInstallPrompt />
        </Suspense>
      )}
      <Suspense fallback={<PageLoader />}>
        <RouteTransition>
          <Switch>
            <Route path="/">
              <ErrorBoundary section="Dashboard"><Dashboard /></ErrorBoundary>
            </Route>
            <Route path="/arquivo">
              <ErrorBoundary section="Arquivo"><ArchivePage /></ErrorBoundary>
            </Route>
            <Route path="/lixeira">
              <ErrorBoundary section="Lixeira"><TrashPage /></ErrorBoundary>
            </Route>
            <Route path="/library">
              <ErrorBoundary section="Biblioteca"><Library /></ErrorBoundary>
            </Route>
            <Route path="/library/trash">
              <ErrorBoundary section="BibliotecaLixeira"><LibraryTrash /></ErrorBoundary>
            </Route>
            <Route path="/library/stats">
              <ErrorBoundary section="BibliotecaStats"><LibraryStats /></ErrorBoundary>
            </Route>
            <Route path="/leituras">
              <RequireProject><ErrorBoundary section="Leituras"><Leituras /></ErrorBoundary></RequireProject>
            </Route>
            <Route path="/repository">
              <ErrorBoundary section="Repositorio"><Repository /></ErrorBoundary>
            </Route>
            <Route path="/dados-pesquisa">
              <RequireProject><ErrorBoundary section="DadosPesquisa"><DadosPesquisa /></ErrorBoundary></RequireProject>
            </Route>
            <Route path="/dados-pesquisa/grupo/:groupId">
              <RequireProject><ErrorBoundary section="GrupoPage"><GrupoPage /></ErrorBoundary></RequireProject>
            </Route>
            <Route path="/dados-pesquisa-v2">
              <ErrorBoundary section="DadosPesquisaV2"><DadosPesquisaV2 /></ErrorBoundary>
            </Route>
            <Route path="/conexoes">
              <RequireProject><ErrorBoundary section="Conexoes"><Conexoes /></ErrorBoundary></RequireProject>
            </Route>
            <Route path="/tecer-texto">
              <ErrorBoundary section="TecerTexto"><TecerTexto /></ErrorBoundary>
            </Route>
            <Route path="/citacoes">
              <RequireProject><ErrorBoundary section="Citacoes"><Citations /></ErrorBoundary></RequireProject>
            </Route>
            <Route path="/indicacoes">
              <ErrorBoundary section="Indicacoes"><Indicacoes /></ErrorBoundary>
            </Route>
            <Route path="/colaboracao">
              <ErrorBoundary section="Colaboracao"><Colaboracao /></ErrorBoundary>
            </Route>
            <Route path="/preferencias">
              <ErrorBoundary section="Preferencias"><Preferencias /></ErrorBoundary>
            </Route>
            <Route path="/admin">
              <ErrorBoundary section="Admin"><AdminPanel /></ErrorBoundary>
            </Route>
            <Route path="/shared/:id">
              <ErrorBoundary section="SharedBiblioteca"><SharedBiblioteca /></ErrorBoundary>
            </Route>
            <Route path="/pdf/:workId">
              <ErrorBoundary section="PDFReader"><PdfReaderPage /></ErrorBoundary>
            </Route>
            <Route>404 - Página não encontrada</Route>
          </Switch>
        </RouteTransition>
      </Suspense>
      <Analytics />
      <SpeedInsights />
    </Sentry.ErrorBoundary>
  );
}

export default App;
